'use client';

import { useTranslation } from 'next-i18next';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { cn } from '@utils/cn';
import { useMissionNavItems } from '@components/features/candidate-prompt-bar/useMissionNavItems';
import { useAddTaskItems } from '@components/layout/addTask';
import { useState } from 'react';

/**
 * Mobile-only fixed bottom navigation: the mission links (Jobs, Coach, Talents, MyJobs+).
 * Replaces the mission chips and the footer. Hidden on lg+ where the mission links render
 * flat in the header (HeaderMissionLinks + MainMenuLinks). The in-flow spacer div reserves
 * the bar's height so page content never hides under it.
 */
export const BottomNav = () => {
  const { t } = useTranslation('common');
  const { locale } = useExtendedRouter();
  const headerInfo = useAppSelector(getUserHeaderInfo);
  // MyJobs+ is a plain /my-jobs link like the other missions — its former popup entries
  // all live on the my-jobs page top bar now.
  const { items, handleClick } = useMissionNavItems(locale, Boolean(headerInfo), true);
  const addTaskItems = useAddTaskItems();
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  const renderLink = ({ item, label }: (typeof items)[number]) => (
    <a
      key={item.key}
      href={item.href}
      aria-current={item.selected ? 'page' : undefined}
      onClick={(event) => {
        // Let the browser handle modifier / middle clicks (open in new tab); intercept
        // plain left-clicks for soft-navigation through handleClick.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        // Tapping the active mission from a sub-page returns to mission home; isOnSamePage
        // (inside handleClick) still prevents same-page churn.
        handleClick({ ...item, selected: false });
      }}
      className={cn(
        'flex flex-1 items-center justify-center whitespace-nowrap px-1 text-sm transition-colors',
        item.selected ? 'font-semibold text-secondary' : 'cursor-pointer text-ink-medium hover:text-ink',
      )}
    >
      {label}
    </a>
  );

  const renderAddJob = ({ item, label }: (typeof items)[number]) => (
    <div key={item.key} className="relative flex flex-1">
      <button
        type="button"
        aria-expanded={addMenuOpen}
        aria-haspopup="menu"
        data-test-id="bottom-nav-add-job"
        onClick={() => setAddMenuOpen((wasOpen) => !wasOpen)}
        className={cn(
          'flex flex-1 items-center justify-center whitespace-nowrap px-1 text-sm transition-colors',
          addMenuOpen || item.selected ? 'font-semibold text-secondary' : 'cursor-pointer text-ink-medium hover:text-ink',
        )}
      >
        {label}
      </button>

      {addMenuOpen && (
        <div
          role="menu"
          // Opens upward: the bar is pinned to the bottom of the viewport, so a
          // panel below it would be off-screen.
          className="absolute bottom-full right-0 z-30 mb-2 min-w-max overflow-hidden rounded-md bg-surface shadow-lg ring-1 ring-ink/10"
        >
          <a
            href={item.href}
            role="menuitem"
            data-test-id="bottom-nav-add-job-item"
            onClick={(event) => {
              if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
              event.preventDefault();
              setAddMenuOpen(false);
              handleClick({ ...item, selected: false });
            }}
            className="flex items-center whitespace-nowrap px-4 py-3 text-sm font-semibold text-ink"
          >
            {t('label.job')}
          </a>

          {/* A single "Task" row: the wording is a plain label and each way into zmeu
              is its own icon link, so the tap target says which channel it opens. */}
          <div className="flex items-center justify-between gap-4 whitespace-nowrap border-t border-ink/5 px-4 py-3 text-sm font-semibold text-ink">
            {t('label.task')}
            <span className="flex items-center gap-4">
              {addTaskItems.map(({
                href, icon: Icon, ariaLabel, dataTestId, external,
              }) => (
                <a
                  key={dataTestId}
                  href={href}
                  role="menuitem"
                  title={ariaLabel}
                  aria-label={ariaLabel}
                  data-test-id={dataTestId}
                  onClick={() => setAddMenuOpen(false)}
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  className="text-green-600"
                >
                  <Icon className="size-6 shrink-0" />
                </a>
              ))}
            </span>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Tapping anywhere else closes the menu — without this it would stay
          open while the user scrolls the page underneath it. */}
      {addMenuOpen && (
        <button
          type="button"
          aria-label="close"
          tabIndex={-1}
          onClick={() => setAddMenuOpen(false)}
          className="fixed inset-0 z-30 cursor-default lg:hidden"
        />
      )}

      <div aria-hidden className="h-[calc(3.5rem+env(safe-area-inset-bottom))] print:hidden lg:hidden" />
      <nav className={cn(
        'fixed inset-x-0 bottom-0 border-t border-input bg-surface pb-[env(safe-area-inset-bottom)] print:hidden lg:hidden',
        addMenuOpen ? 'z-40' : 'z-20',
      )}
      >
        <div className="flex h-14 items-stretch">
          {items.map((entry) => (entry.item.key === 'add-job' ? renderAddJob(entry) : renderLink(entry)))}
        </div>
      </nav>
    </>
  );
};
